import { useEffect, useRef, useState, useCallback } from 'react';
import { StyleSheet, Dimensions, Keyboard, Platform, TextInput } from 'react-native';
import VerifyPanHeader from './component/Header/Header';
import InputText from '@sharedComponents/TextInput/TextInput';
import Button from '@sharedComponents/Button/Button';
import gs from '../../styles/globalStyles';
import BottomSheetContainer from '@sharedComponents/Bottomsheet/Bottomsheet';
import ConfirmPanDetails from './component/ConfirmPanDetails/ConfirmPanDetails';
import { CheckCircle } from '../../sharedComponents/Icons';
import dayjs from 'dayjs';
import FadeInAnimation from '@sharedComponents/FadinAnimation/FadinAnimation';
import axios from 'axios';
import { endpoints } from '../../api/apis';
import { showErrorSheet } from '../../store/slices/errorSlice';
import SecureIcon from '../../../assets/Icons/Secure';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { PrivateStackParamList } from '@routes/types';
import KeyboardDismissView from '@hoc/KeyboardDismissView';
import ScreenWrapper from '@hoc/ScreenWrapper';
import {trackEvent} from '../../utils'
import { useAppDispatch , useAppSelector } from '../../store/hooks';
import { ThemedText, ThemedView } from '@app/theme';

import { updateUserDetailLinkedApp, updateUserDetailPan } from '../../store/slices/authSlice';
import {useDarkMode} from '@app/utils';

var customParseFormat = require('dayjs/plugin/customParseFormat');
dayjs.extend(customParseFormat);
// Regular expression to validate PAN card format
const panCardRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
const screenWidth = Dimensions.get('window').width;

type ScreenProps = NativeStackScreenProps<PrivateStackParamList ,'KYC_PAN_VERIFICATION'>

const VerifyPan = ({ navigation, route } : ScreenProps) => {
  // State variables
  const [panNumber, setPanNumber] = useState('');
  const [panError, setPanError] = useState('');
  const [isPanValid, setIsPanValid] = useState(false);
  const [isDobValid, setIsDobValid] = useState(false);
  const [dob, setDob] = useState(0);
  const [displayDateError, setDisplayDateError] = useState(false);
  const [buttonLoader, setButtonLoader] = useState(false);
  const [isBottomSheetVisible, setIsBottomSheetVisible] = useState(false);
  const [day, setDay] = useState('');
  const [month, setMonth] = useState('');
  const [year, setYear] = useState('');
  const [dobError, setDobError] = useState('');
  const [verifiedPanData, setVerifiedPanData] = useState({});
  const [updatedApplicationObj,setUpdatedApplicationObj] = useState({})
  const [resetPanLoader, setResetPanLoader] = useState(false);

  const dispatch = useAppDispatch();
  const userDetails = useAppSelector(state => state.auth.userDetails);
  const panRef = useRef<TextInput>(null);
  const displayDobError = displayDateError && !isDobValid;
  const {isErrorSheetVisible} = useAppSelector(state => state.errorSheet)
  // Refs to control the focus
  const dateInputRef = useRef<TextInput>(null);
  const monthInputRef = useRef<TextInput>(null);
  const yearInputRef = useRef<TextInput>(null);

  const isDarkMode = useDarkMode()


  // useEffect(() => {
  //   if (isErrorSheetVisible===false) {
  //     setTimeout(()=>{
  //       focusOnPan()
  //     },300)
  //   }
  // }, [isErrorSheetVisible]);

  const focusOnPan = () => {
    if (panRef.current) {
      setTimeout(() => {
        panRef?.current?.focus();
      }, 400)
    }
  }

  useEffect(() => {
    if(userDetails?.linkedApplications[0]?.panNumber){
      setPanNumber(userDetails?.linkedApplications[0]?.panNumber || '')
    }
    focusOnPan()
    return () => {
      closeBottomSheet();
    };
  }, []);

  // Automatically focus to the next input after filling each field
  const handleDayChange = text => {
    let formattedText = text.replace(/[^0-9]/g, '');
    setDay(formattedText);
    if (formattedText.length === 2) {
      monthInputRef?.current?.focus();
    }
  };

  const handleMonthChange = text => {
    let formattedText = text.replace(/[^0-9]/g, '');
    setMonth(formattedText);
    if (formattedText.length === 2) {
      yearInputRef?.current?.focus();
    }
    if (formattedText === '' && text.length === 0) {
      dateInputRef?.current?.focus();
    }
  };

  const handleYearChange = text => {
    let formattedText = text.replace(/[^0-9]/g, '');
    setYear(formattedText);
    if(formattedText.length === 4) Keyboard.dismiss()
    if (formattedText === '' && text.length === 0) {
        monthInputRef?.current?.focus();
    }
  };

  // Function to close the bottom sheet
  const closeBottomSheet = useCallback(() => {
    setIsBottomSheetVisible(false);
       
    trackEvent('KYC_PAN_VERIFICATION_SHEET_CLOSED')
  }, []);

  // Function to navigate to the Check Eligibility screen
  const gotoCheckEligibility = () => {
    navigation.navigate('CheckEligibility');
  };

  useEffect(() => {
    const isDobComplete =
      day.length === 2 && month.length === 2 && year.length === 4;
    setDisplayDateError(isDobComplete);
    if (
      isDobComplete &&
      dayjs(`${year}-${month}-${day}`, 'YYYY-MM-DD', true).isValid()
    ) {
      const currentDate = Date.now();
      const minDate = currentDate - 100 * 31557600000; // 100 years in milliseconds
      const maxDate = currentDate - 18 * 31557600000; // 18 years in milliseconds
      const dateOfbirth = dayjs(`${year}-${month}-${day}`).valueOf();
      const isDateOfbirthValid =
        dateOfbirth >= minDate && dateOfbirth <= maxDate;
      setIsDobValid(isDateOfbirthValid);
      setDob(dateOfbirth);
      setDobError(isDateOfbirthValid ? '' : 'Age should be between 18 and 100');
    } else {
      setDob(0);
      setIsDobValid(false);
      setDobError('Invalid date.Please enter a valid date of birth');
    }
  }, [day, month, year]);

  let debounceTimeout: NodeJS.Timeout | null = null; 

  const onInput = (text: string) => {
    const hasLowercase = /[a-z]/.test(text);
  
    if (hasLowercase) {
      if (debounceTimeout) clearTimeout(debounceTimeout);
  
      debounceTimeout = setTimeout(() => {
        processInput(text);
      },0); 
    } else {
      processInput(text);
    }
  };
  
  const processInput = (text: string) => {
    let inputText = text.toUpperCase().replace(/[^A-Z0-9]/g, ''); 
    if (inputText.length > 10) {
      inputText = inputText.slice(0, 10); 
    }
    setPanNumber(inputText);
    if (!panCardRegex.test(inputText) && inputText.length === 10) {
      setIsPanValid(false);
      setPanError('Enter a valid PAN');
    } else {
      setIsPanValid(inputText.length === 10);
      setPanError('');
    }
  };

  // Function to handle the continue button press
  const fetchPanDetails = () => {
    setButtonLoader(true);
    const payload = {
      panNumber,
      dob,
      applicationId: userDetails?.linkedApplications[0]?.applicationId,
    };
    axios
      .post(endpoints.verifyPan, payload, { handleFromMF: true })
      .then(res => {
        console.log('fetchPanDetails', res.data);
        setVerifiedPanData(res?.data?.stepResponseObject);
        setUpdatedApplicationObj(res?.data?.updatedApplicationObj)
        setIsBottomSheetVisible(true);
        trackEvent('KYC_PAN_VERIFICATION_SHEET_LOADED')
      })
      .catch(err => {
        panRef?.current?.blur();
        dispatch(
          showErrorSheet({
            isErrorSheetVisible: true,
            errorMessage: err?.data?.voltErrorCode === 'TOO_MANY_REQUESTS' ? 'Something went wrong' : err?.data?.message,
            closeOnBackdropPress: true,
            errorType: err?.data?.voltErrorCode || err?.data?.statusCode,
          }),
        );
        console.log('err', err);
      })
      .finally(() => {
        setButtonLoader(false);
      });
  };

  const confirmPan = () => {
    closeBottomSheet();
    panNumber && dispatch(updateUserDetailPan(panNumber));
    if(Platform.OS === 'ios') {
       dispatch(updateUserDetailLinkedApp(updatedApplicationObj));   
        navigation.reset({
          index: 0,
          routes: [{ name: 'HOME' }, { name: 'MF_FETCH_PORTFOLIO' }]
        })
    } else {
    setTimeout(() => {
      dispatch(updateUserDetailLinkedApp(updatedApplicationObj));
      // const currentStepId = userDetails?.linkedApplications[0]?.currentStepId;
      // To switch to old architecture
      if (Platform.OS === 'web') {
        navigation.navigate('MF_FETCH_PORTFOLIO');
      } else {
        navigation.reset({
          index: 0,
          routes: [{ name: 'HOME' }, { name: 'MF_FETCH_PORTFOLIO' }]
        })
      }
    }, 100);
    }
  };

  const resetPan = () => {
    setResetPanLoader(true)
    const applicationId = userDetails?.linkedApplications[0]?.applicationId;
    const url = `${endpoints.resetPan}${applicationId}`;
    // setVerifiedPanData({});
    // setUpdatedApplicationObj({});
    axios
      .post(url, {})
      .then(res => {})
      .catch(err => {
        dispatch(
          showErrorSheet({
            isErrorSheetVisible:true,
            errorMessage: err?.data?.message,
            closeOnBackdropPress: true,
            errorType: err?.data?.voltErrorCode || err?.data?.statusCode,
          }),
        );
        console.log('err', err);
      })
      .finally(() => {
        setResetPanLoader(false)
        closeBottomSheet();
       
      });
  };

  return (

    <ScreenWrapper screenName={route?.name} additionalProperties={{newArchRoute : true}}>
    <KeyboardDismissView>
    <ThemedView flex={1}>
      {/* Header component */}
      <VerifyPanHeader pageName={route?.name} navigation={navigation} />
      <ThemedView
       flex={1}
       bg='bg-surface-primary'
       padding='lg'
       >
        {/* Title */}
          <ThemedText variant='h3b' color='text-primary' mb="sm">
            Verify your PAN
          </ThemedText>
        {/* Information box */}
        <ThemedView
          flexDirection='row'
          alignItems='center'
          backgroundColor='bg-fill-emphasis-secondary'
          p='sm'
          borderRadius='xs'
          mb='xl'
          >
          <SecureIcon size="12" />
          <ThemedView style={{marginLeft:4,flexDirection:'row'}}>
            <ThemedText variant='b4r' color='text-primary' >We need your PAN to comply with</ThemedText>
            <ThemedText variant='b4s' color='text-primary'> RBI </ThemedText>
            <ThemedText variant='b4r' color='text-primary'>regulations</ThemedText>
          </ThemedView>
        </ThemedView>
        {/* PAN input field */}
        <InputText
          autoCapitalize="characters"
          ref={panRef}
          placeHolder={'Enter PAN'}
          keyboardType="default"
          label="PAN"
          showLabel={true}
          value={panNumber}
          onChangeText={onInput}
          onFocus={()=>{
            trackEvent('KYC_PAN_VERIFICATION_PAN_INPUT_FOCUSED');
          }}
          showErrorMessage={true}
          showErrorMessageInFocus={true}
          isDarkMode={Boolean(isDarkMode)}
          hasError={panError}
          showRightAccessory={panNumber?.length>0}
          rightAccessory={() => {
            return (
              <>
                {isPanValid ? (
                  <>
                    <CheckCircle
                      size={24}
                      weight="fill"
                      fill="rgb(18, 183, 106)"
                    />
                  </>
                ) : (
                  <ThemedText
                    variant='b2r'
                    color='text-secondary'
                    mr="sm"
                    >
                    {panNumber?.length}/10
                  </ThemedText>
                )}
              </>
            );
          }}
        />
        {/* Date of Birth input field */}
          <ThemedText variant='b3s' color='text-primary' mb="md">
          Date of birth (as per PAN)
          </ThemedText>
        <ThemedView style={{flex : Platform.OS !== 'web' ? 1 : 0}} >
          {/* Date of Birth validator component */}
          <ThemedView  flexDirection="row" justifyContent='space-between'>
            <ThemedView flex={1}>
              <InputText
                label="Date"
                showLabel={true}
                placeHolder="DD"
                keyboardType="numeric"
                maxLength={2}
                value={day}
                isDarkMode={Boolean(isDarkMode)}
                onChangeText={handleDayChange}
                hasError={displayDobError}
                ref={dateInputRef}
                onFocus={()=>{
                  trackEvent('KYC_PAN_VERIFICATION_DATE_INPUT_FOCUSED');
                }}
              />
            </ThemedView>
            <ThemedView flex={1} mx="lg">
              <InputText
                label="Month"
                showLabel={true}
                placeHolder="MM"
                keyboardType="numeric"
                maxLength={2}
                value={month}
                isDarkMode={Boolean(isDarkMode)}
                onChangeText={handleMonthChange}
                ref={monthInputRef}
                hasError={displayDobError}
                onFocus={()=>{
                  trackEvent('KYC_PAN_VERIFICATION_MONTH_INPUT_FOCUSED');
                }}
              />
            </ThemedView>
            <ThemedView  flex={1}>
              <InputText
                label="Year"
                showLabel={true}
                placeHolder="YYYY"
                keyboardType="numeric"
                maxLength={4}
                isDarkMode={Boolean(isDarkMode)}
                value={year}
                onChangeText={handleYearChange}
                ref={yearInputRef}
                hasError={displayDobError}
                onFocus={()=>{
                  trackEvent('KYC_PAN_VERIFICATION_YEAR_INPUT_FOCUSED');
                }}
              />
            </ThemedView>
          </ThemedView>
          <ThemedView my="sm">
            {/* Error message for invalid date of birth */}
            {displayDobError && (
              <FadeInAnimation delay={100} duration={500}>
                <ThemedText color='text-error' variant='b2r' >
                  {dobError}
                </ThemedText>
              </FadeInAnimation>
            )}
          </ThemedView>
        </ThemedView>
         {/* Continue button */}
      <ThemedView backgroundColor='bg-surface-primary' style={[Platform.OS === 'web' ? ls.webBtn:ls.nativeBtn ]}>
        <ThemedView height={1} width={screenWidth} backgroundColor='border-primary' />
        <ThemedView m="lg">
          <Button
            eventName={route?.name + '_CONTINUE'}
            label={'Continue'}
            handlePress={fetchPanDetails}
            type={'SOLID'}
            size='MD'
            disabled={!isPanValid || !isDobValid || buttonLoader}
            buttonLoader={buttonLoader}
          />
        </ThemedView>
      </ThemedView>
      </ThemedView>
     
      {/* Bottom sheet container */}
      <BottomSheetContainer
        isVisible={isBottomSheetVisible}
        close={closeBottomSheet}
        closeOnBackdropPress={false}
        borderTopRightRadius={10}
        borderTopLeftRadius={10}
            onClose={() => {
            
            }}
        
        containerStyle={gs.setBg('#fefefe')}>
        <ConfirmPanDetails
          resetPan={resetPan}
          onClose={closeBottomSheet}
          verifiedPanData={verifiedPanData}
          confirmPan={confirmPan}
          resetPanLoader={resetPanLoader}


        />
      </BottomSheetContainer>
    </ThemedView>
    </KeyboardDismissView>
    </ScreenWrapper>
  );
};

export default VerifyPan;
const ls = StyleSheet.create({
  nativeBtn:{
    position:'absolute',
    bottom:0,
  },
  webBtn:{
    position: screenWidth>450 ?'absolute':'fixed',
    bottom:0,
    left:0,
    width:'100%'
  }
});
